<script setup lang="ts">
import { useRouter } from "vue-router";
import FileReport from "@/components/FileReport.vue";

const router = useRouter();

const selectStudent = () => {
  router.push({ name: "student-register" });
};

const selectTutor = () => {
  router.push({ name: "tutor-register" });
};
</script>

<template>
  <FileReport />

  <div class="register-role-page">
    <!-- Header Section -->
    <div class="page-header">
      <h1>Choose Your Path</h1>
      <p>Whether you're looking to learn or teach, we have the experience for you.</p>
    </div>

    <!-- Role Selection Cards -->
    <div class="role-cards-container">
      <!-- Student Card -->
      <div class="role-card student-card">
        <div class="card-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="size-5"
          >
            <path
              fillRule="evenodd"
              d="M9.664 1.319a.75.75 0 0 1 .672 0 41.059 41.059 0 0 1 8.198 5.424.75.75 0 0 1-.254 1.285 31.372 31.372 0 0 0-7.86 3.83.75.75 0 0 1-.84 0 31.508 31.508 0 0 0-2.08-1.287V9.394c0-.244.116-.463.302-.592a35.504 35.504 0 0 1 3.305-2.033.75.75 0 0 0-.714-1.319 37 37 0 0 0-3.446 2.12A2.216 2.216 0 0 0 6 9.393v.38a31.293 31.293 0 0 0-4.28-1.746.75.75 0 0 1-.254-1.285 41.059 41.059 0 0 1 8.198-5.424ZM6 11.459a29.848 29.848 0 0 0-2.455-1.158 41.029 41.029 0 0 0-.39 3.114.75.75 0 0 0 .419.74c.528.256 1.046.53 1.554.82-.21.324-.455.63-.739.914a.75.75 0 1 0 1.06 1.06c.37-.369.69-.77.96-1.193a26.61 26.61 0 0 1 3.095 2.348.75.75 0 0 0 .992 0 26.547 26.547 0 0 1 5.93-3.95.75.75 0 0 0 .42-.739 41.053 41.053 0 0 0-.39-3.114 29.925 29.925 0 0 0-5.199 2.801 2.25 2.25 0 0 1-2.514 0c-.41-.275-.826-.541-1.25-.797a6.985 6.985 0 0 1-1.084 3.45 26.503 26.503 0 0 0-1.281-.78A5.487 5.487 0 0 0 6 12v-.54Z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <h2>I'm a Student</h2>
        <p class="card-description">Find expert tutors and improve your grades</p>

        <div class="features-list">
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Access to verified tutors</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Smart tutor matching</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Flexible scheduling system</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>AI session summaries & study materials</span>
          </div>
        </div>

        <div class="engagement-box">
          <span>Join students already improving their grades.</span>
        </div>

        <button class="role-button student-button" @click="selectStudent">
          Register as Student
        </button>
      </div>

      <!-- Tutor Card -->
      <div class="role-card tutor-card">
        <div class="card-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
            />
          </svg>
        </div>

        <h2>I'm a Tutor</h2>
        <p class="card-description">Share your knowledge and make a difference</p>

        <div class="features-list">
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Flexible schedule management</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Student matching algorithm</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Professional development support</span>
          </div>
          <div class="feature-item">
            <svg class="checkmark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>Competitive compensation</span>
          </div>
        </div>

        <div class="engagement-box">
          <span>Join tutors in making a difference.</span>
        </div>

        <button class="role-button tutor-button" @click="selectTutor">Apply as Tutor</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.register-role-page {
  min-height: 100vh;
  background: url("/register-role-bg.png") no-repeat center center;
  background-size: cover;
  padding: 60px 40px;
  padding-top: 160px; /* Add top padding to account for header height */
  padding-bottom: 80px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Header Section */
.page-header {
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.page-header h1 {
  color: #000000;
  font-size: 48px;
  font-weight: 800;
  margin: 0 0 16px 0;
  line-height: 1.2;
  text-shadow: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  letter-spacing: -0.01em;
}

.page-header p {
  color: #000000;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
  margin-left: auto;
  margin-right: auto;
  text-shadow: none;
  font-weight: 400;
  font-style: italic;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  max-width: 600px;
  white-space: nowrap;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .register-role-page {
    padding: 40px 20px;
    padding-top: 120px;
  }

  .page-header {
    margin-bottom: 30px;
  }

  .page-header h1 {
    font-size: 32px;
    margin-bottom: 12px;
  }

  .page-header p {
    font-size: 14px;
    white-space: normal;
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .register-role-page {
    padding: 30px 15px;
    padding-top: 100px;
  }

  .page-header h1 {
    font-size: 28px;
    margin-bottom: 10px;
  }

  .page-header p {
    font-size: 13px;
    line-height: 1.4;
  }
}

/* Role Cards Mobile Responsive */
@media (max-width: 1200px) {
  .role-cards-container {
    gap: 50px;
    max-width: 100%;
    padding: 0 30px;
  }

  .role-card {
    padding: 23px 60px;
    border-radius: 26px;
  }

  .card-icon {
    width: 75px;
    height: 75px;
  }

  .card-icon svg {
    width: 45px;
    height: 45px;
  }

  .role-card h2 {
    font-size: 23px;
    margin: -11px 0 8px 0;
  }

  .card-description {
    font-size: 14px;
    margin: -3px 0 12px 0;
  }

  .feature-item {
    margin: 4px 0 8px 0;
  }

  .feature-item span {
    font-size: 12px;
  }

  .engagement-box {
    padding: 14px 18px;
    font-size: 12px;
    margin-bottom: 10px;
  }

  .role-button {
    padding: 12px 24px;
    font-size: 14px;
  }
}

@media (max-width: 1024px) {
  .role-cards-container {
    gap: 40px;
    max-width: 100%;
    padding: 0 20px;
  }

  .role-card {
    padding: 22px 50px;
    border-radius: 24px;
  }

  .card-icon {
    width: 70px;
    height: 70px;
  }

  .card-icon svg {
    width: 42px;
    height: 42px;
  }

  .role-card h2 {
    font-size: 22px;
    margin: -10px 0 7px 0;
  }

  .card-description {
    font-size: 13px;
    margin: -3px 0 11px 0;
  }

  .feature-item {
    margin: 3px 0 7px 0;
  }

  .feature-item span {
    font-size: 11px;
  }

  .engagement-box {
    padding: 14px 18px;
    font-size: 11px;
    margin-bottom: 8px;
  }

  .role-button {
    padding: 12px 24px;
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .role-cards-container {
    flex-direction: column;
    gap: 40px;
    max-width: 100%;
    padding: 0 15px;
    margin-bottom: 40px;
  }

  .role-card {
    max-width: 100%;
    width: 100%;
    padding: 20px 25px;
    min-height: auto;
    border-radius: 20px;
    margin: 0 auto;
  }

  .card-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 0px;
  }

  .card-icon svg {
    width: 36px;
    height: 36px;
  }

  .role-card h2 {
    font-size: 20px;
    margin: -8px 0 6px 0;
  }

  .card-description {
    font-size: 13px;
    margin: -4px 0 10px 0;
  }

  .feature-item {
    margin: 3px 0 6px 0;
  }

  .feature-item span {
    font-size: 11px;
  }

  .engagement-box {
    padding: 14px 18px;
    font-size: 11px;
    margin-bottom: 8px;
  }

  .role-button {
    padding: 12px 22px;
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .role-cards-container {
    gap: 35px;
    padding: 0 12px;
    margin-bottom: 35px;
  }

  .role-card {
    width: 100%;
    padding: 18px 22px;
    border-radius: 18px;
    margin: 0 auto;
  }

  .card-icon {
    width: 55px;
    height: 55px;
  }

  .card-icon svg {
    width: 33px;
    height: 33px;
  }

  .role-card h2 {
    font-size: 19px;
    margin: -7px 0 5px 0;
  }

  .card-description {
    font-size: 12px;
    margin: -3px 0 9px 0;
  }

  .feature-item {
    margin: 2px 0 5px 0;
  }

  .feature-item span {
    font-size: 10px;
  }

  .engagement-box {
    padding: 12px 16px;
    font-size: 10px;
    margin-bottom: 8px;
  }

  .role-button {
    padding: 11px 20px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .role-cards-container {
    gap: 30px;
    padding: 0 10px;
    margin-bottom: 30px;
  }

  .role-card {
    width: 100%;
    padding: 16px 18px;
    border-radius: 16px;
    margin: 0 auto;
  }

  .card-icon {
    width: 50px;
    height: 50px;
  }

  .card-icon svg {
    width: 30px;
    height: 30px;
  }

  .role-card h2 {
    font-size: 18px;
    margin: -6px 0 4px 0;
  }

  .card-description {
    font-size: 12px;
    margin: -2px 0 8px 0;
  }

  .feature-item {
    margin: 2px 0 4px 0;
  }

  .feature-item span {
    font-size: 10px;
  }

  .engagement-box {
    padding: 10px 14px;
    font-size: 10px;
    margin-bottom: 6px;
  }

  .role-button {
    padding: 10px 18px;
    font-size: 12px;
  }
}

@media (max-width: 360px) {
  .role-cards-container {
    gap: 25px;
    padding: 0 8px;
    margin-bottom: 25px;
  }

  .role-card {
    width: 100%;
    padding: 14px 16px;
    border-radius: 14px;
    margin: 0 auto;
  }

  .card-icon {
    width: 45px;
    height: 45px;
  }

  .card-icon svg {
    width: 27px;
    height: 27px;
  }

  .role-card h2 {
    font-size: 16px;
    margin: -5px 0 3px 0;
  }

  .card-description {
    font-size: 11px;
    margin: -1px 0 6px 0;
  }

  .feature-item {
    margin: 1px 0 3px 0;
  }

  .feature-item span {
    font-size: 9px;
  }

  .engagement-box {
    padding: 8px 12px;
    font-size: 9px;
    margin-bottom: 6px;
  }

  .role-button {
    padding: 9px 16px;
    font-size: 11px;
  }
}

@media (max-width: 320px) {
  .role-cards-container {
    gap: 20px;
    padding: 0 5px;
    margin-bottom: 20px;
  }

  .role-card {
    width: 100%;
    padding: 12px 14px;
    border-radius: 12px;
    margin: 0 auto;
  }

  .card-icon {
    width: 40px;
    height: 40px;
  }

  .card-icon svg {
    width: 24px;
    height: 24px;
  }

  .role-card h2 {
    font-size: 15px;
    margin: -4px 0 2px 0;
  }

  .card-description {
    font-size: 10px;
    margin: -1px 0 5px 0;
  }

  .feature-item {
    margin: 1px 0 2px 0;
  }

  .feature-item span {
    font-size: 8px;
  }

  .engagement-box {
    padding: 6px 10px;
    font-size: 8px;
    margin-bottom: 4px;
  }

  .role-button {
    padding: 8px 14px;
    font-size: 10px;
  }
}

/* Landscape orientation for mobile devices */
@media (max-width: 768px) and (orientation: landscape) {
  .role-cards-container {
    flex-direction: row;
    gap: 20px;
    padding: 0 10px;
    margin-bottom: 20px;
  }

  .role-card {
    flex: 1;
    padding: 15px 20px;
    min-height: 200px;
  }

  .card-icon {
    width: 50px;
    height: 50px;
  }

  .card-icon svg {
    width: 30px;
    height: 30px;
  }

  .role-card h2 {
    font-size: 16px;
    margin: -6px 0 4px 0;
  }

  .card-description {
    font-size: 11px;
    margin: -2px 0 6px 0;
  }

  .feature-item {
    margin: 1px 0 3px 0;
  }

  .feature-item span {
    font-size: 9px;
  }

  .engagement-box {
    padding: 6px 10px;
    font-size: 9px;
    margin-bottom: 6px;
  }

  .role-button {
    padding: 8px 14px;
    font-size: 10px;
  }
}

/* Role Cards Container */
.role-cards-container {
  display: flex;
  gap: 80px;
  justify-content: center;
  align-items: stretch;
  max-width: 8600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* Role Card */
.role-card {
  background: white;
  border-radius: 28px;
  padding: 24px 40px;
  box-shadow: 8px 8px 0px #000;
  flex: 1;
  max-width: 500px;
  min-width: 450px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  border: 2px solid #000000;
}

.role-card:hover {
  transform: translateY(-2px);
  box-shadow: 10px 10px 0px #000;
}

/* Card Icon */
.card-icon {
  width: 80px;
  height: 80px;
  background: transparent;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0px;
  color: #000000;
}

.card-icon svg {
  width: 48px;
  height: 48px;
}

/* Card Title and Description */
.role-card h2 {
  color: #000000;
  font-size: 24px;
  font-weight: 800;
  margin: -12px 0 8px 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.card-description {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.4;
  margin: -4px 0 12px 0;
  max-width: 4000px;
  font-weight: 400;
}

/* Features List */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
  width: 100%;
  max-width: 4000px;
  padding-left: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  margin: 4px 0 8px 0;
}

.checkmark {
  width: 18px;
  height: 18px;
  color: #10b981;
  flex-shrink: 0;
}

.feature-item span {
  color: #374151;
  font-size: 12px;
  line-height: 1.3;
  font-weight: 500;
}

/* Engagement Box */
.engagement-box {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  color: #1e40af;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  width: calc(100% - 32px);
  max-width: calc(100% - 32px);
  white-space: normal;
  text-align: center;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  box-sizing: border-box;
  margin-left: 16px;
  margin-right: 16px;
}

/* Role Button */
.role-button {
  background: #4f46e5;
  color: white;
  border: 3px solid #000000;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  width: calc(100% - 32px);
  max-width: calc(100% - 32px);
  margin-top: auto;
  margin-left: 16px;
  margin-right: 16px;
  text-align: center;
  box-shadow: 4px 4px 0px #000000;
  box-sizing: border-box;
}

.student-button {
  background: #4f46e5;
}

.tutor-button {
  background: #4f46e5;
}

/* Tutor-specific engagement box styling */
.tutor-card .engagement-box {
  padding: 14px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  width: calc(100% - 32px);
  max-width: calc(100% - 32px);
  box-sizing: border-box;
  margin-left: 16px;
  margin-right: 16px;
}

/* Tutor-specific button styling */
.tutor-card .role-button {
  padding: 14px 20px;
  width: calc(100% - 32px);
  max-width: calc(100% - 32px);
  margin-left: 16px;
  margin-right: 16px;
}

.role-button:hover {
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.role-button:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 1280px) {
  .role-cards-container {
    gap: 60px;
    max-width: 1100px;
  }

  .role-card {
    padding: 20px 30px;
    min-height: 200px;
    max-width: 480px;
    min-width: 420px;
  }

  .page-header h1 {
    font-size: 42px;
  }
}

@media (max-width: 1024px) {
  .role-cards-container {
    gap: 50px;
  }

  .role-card {
    padding: 18px 25px;
    border-width: 2px;
    max-width: 420px;
    min-width: 380px;
  }

  .page-header {
    margin-bottom: 40px;
  }

  .page-header h1 {
    font-size: 36px;
  }

  .role-card h2 {
    font-size: 22px;
  }
}

@media (max-width: 768px) {
  .register-role-page {
    padding: 40px 24px;
    padding-top: 120px; /* Slightly less top padding on mobile */
    background-attachment: scroll; /* Better mobile performance */
    background-position: center top; /* Adjust background position for mobile */
  }

  .page-header h1 {
    font-size: 42px;
  }

  .page-header p {
    font-size: 18px;
  }

  .role-cards-container {
    flex-direction: column;
    gap: 40px;
    align-items: center;
  }

  .role-card {
    max-width: 100%;
    width: 100%;
    min-width: auto;
    min-height: auto;
  }

  .card-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }

  .card-icon svg {
    width: 36px;
    height: 36px;
  }

  .role-card h2 {
    font-size: 24px;
  }

  .features-list {
    max-width: 100%;
  }

  .engagement-box {
    max-width: 100%;
  }

  .role-button {
    max-width: 100%;
    margin-top: 20px;
  }
}

@media (max-width: 480px) {
  .register-role-page {
    padding: 20px 16px;
    padding-top: 100px; /* Even less top padding on small mobile */
  }

  .page-header h1 {
    font-size: 36px;
    margin-bottom: 12px;
  }

  .page-header p {
    font-size: 16px;
  }

  .role-card {
    padding: 16px 14px;
    border-width: 2px;
  }

  .card-icon {
    width: 50px;
    height: 50px;
    margin-bottom: 12px;
  }

  .card-icon svg {
    width: 30px;
    height: 30px;
  }

  .role-card h2 {
    font-size: 20px;
  }

  .card-description {
    font-size: 14px;
    margin-bottom: 20px;
  }

  .features-list {
    gap: 8px;
    margin-bottom: 20px;
  }

  .feature-item span {
    font-size: 13px;
  }

  .engagement-box {
    padding: 10px 14px;
    font-size: 12px;
    margin-bottom: 20px;
  }

  .role-button {
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 12px;
  }
}
</style>
